import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import './cart.scss';
import FurnitureNavbar from '../../../../components/layout/Nav';
import withAuth from '../../../auth/WithAuth';
import { serverUrlAuth } from '../../../../serverUrl';
import { toast } from 'react-toastify';

const CartPage = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  
  // Modal states
  const [showClearCartModal, setShowClearCartModal] = useState(false);
  const [showRemoveSelectedModal, setShowRemoveSelectedModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch cart items from API
  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);
      // Add 'projects/' prefix here
      const response = await serverUrlAuth.get('cart/');
      console.log('Cart response:', response.data);
      
      // Check if response.data has items array
      const cartData = response.data;
      const cartItemsArray = cartData.items || []; // The items are in the 'items' property
      
      // Transform API data to match component structure
      const transformedItems = cartItemsArray.map(item => ({
        id: item.id,
        name: item.product?.name || 'Product',
        category: item.product?.category || 'General',
        price: parseFloat(item.product?.price || 0),
        quantity: item.quantity,
        image: item.product?.images?.[0]?.image || item.product?.images?.[0] || '🛒',
        inStock: (item.product?.stock || 0) > 0,
        stockCount: item.product?.stock || 0,
        productId: item.product?.id
      }));
      
      console.log('Transformed items:', transformedItems);
      setCartItems(transformedItems);
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Failed to load cart items');
    } finally {
      setLoading(false);
    }
  };

  // Update select all state when selections change
  useEffect(() => {
    const availableItems = cartItems.filter((item) => item.inStock);
    const selectedAvailableItems = Array.from(selectedItems).filter(
      (id) => cartItems.find((item) => item.id === id)?.inStock,
    );

    if (
      availableItems.length > 0 &&
      selectedAvailableItems.length === availableItems.length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedItems, cartItems]);

  const toggleItemSelection = (itemId) => {
    const item = cartItems.find((i) => i.id === itemId);
    if (!item?.inStock) return; // Can't select out of stock items

    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      // Deselect all available items
      setSelectedItems(new Set());
    } else {
      // Select all available items
      const availableItemIds = cartItems
        .filter((item) => item.inStock)
        .map((item) => item.id);
      setSelectedItems(new Set(availableItemIds));
    }
  };

  // FIXED: Use the update endpoint for quantity changes
  const updateQuantity = async (id, newQuantity) => {
    if (newQuantity < 1) return;
    
    const item = cartItems.find((i) => i.id === id);
    if (item && newQuantity > item.stockCount) {
      toast.error(`Only ${item.stockCount} items available in stock`);
      return;
    }

    try {
      // Use PATCH to update quantity with the new endpoint
      await serverUrlAuth.patch(`cart/update/${id}/`, {
        quantity: newQuantity
      });
      
      // Update local state
      setCartItems((items) =>
        items.map((item) =>
          item.id === id ? { ...item, quantity: newQuantity } : item,
        )
      );
      
      toast.success('Cart updated');
    } catch (error) {
      console.error('Error updating cart:', error);
      toast.error(error.response?.data?.error || 'Failed to update cart');
    }
  };

  const removeItem = async (id) => {
    try {
      // Add 'projects/' prefix here
      await serverUrlAuth.delete(`cart/remove/${id}/`);
      
      setCartItems((items) => items.filter((item) => item.id !== id));
      setSelectedItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      
      toast.success('Item removed from cart');
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error('Failed to remove item');
    }
  };

  const handleRemoveSelected = () => {
    if (selectedItems.size === 0) return;
    setShowRemoveSelectedModal(true);
  };

  const confirmRemoveSelected = async () => {
    setActionLoading(true);
    try {
      // Remove items one by one
      for (const id of selectedItems) {
        await serverUrlAuth.delete(`cart/remove/${id}/`);
      }
      
      setCartItems((items) =>
        items.filter((item) => !selectedItems.has(item.id)),
      );
      setSelectedItems(new Set());
      setShowRemoveSelectedModal(false);
      toast.success('Selected items removed');
    } catch (error) {
      console.error('Error removing items:', error);
      toast.error('Failed to remove some items');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearCart = () => {
    setShowClearCartModal(true);
  };

  const confirmClearCart = async () => {
    setActionLoading(true);
    try {
      // Remove all items one by one
      for (const item of cartItems) {
        await serverUrlAuth.delete(`cart/remove/${item.id}/`);
      }
      
      setCartItems([]);
      setSelectedItems(new Set());
      setShowClearCartModal(false);
      toast.success('Cart cleared');
    } catch (error) {
      console.error('Error clearing cart:', error);
      toast.error('Failed to clear cart');
    } finally {
      setActionLoading(false);
    }
  };

  // Calculate totals based on selected items only
  const calculateSelectedSubtotal = () => {
    return cartItems
      .filter((item) => selectedItems.has(item.id))
      .reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const calculateSelectedDiscount = () => {
    return promoApplied ? calculateSelectedSubtotal() * 0.2 : 0;
  };

  const calculateSelectedTax = () => {
    return (calculateSelectedSubtotal() - calculateSelectedDiscount()) * 0.16;
  };

  const calculateSelectedTotal = () => {
    return (
      calculateSelectedSubtotal() -
      calculateSelectedDiscount() +
      calculateSelectedTax()
    );
  };

  const handleCheckout = () => {
    if (selectedItems.size === 0) {
      toast.warning('Please select items to checkout');
      return;
    }
    // Navigate to checkout page with selected items
    navigate('/checkout', { 
      state: { 
        selectedItems: Array.from(selectedItems),
        total: calculateSelectedTotal() 
      } 
    });
  };

  const selectedCount = selectedItems.size;
  const availableItemsCount = cartItems.filter((item) => item.inStock).length;

  if (loading) {
    return (
      <>
        <FurnitureNavbar />
        <div className="cart-page">
          <div className="cart-container">
            <div className="cart-loading">
              <div className="spinner"></div>
              <p>Loading your cart...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (cartItems.length === 0) {
    return (
      <>
        <FurnitureNavbar />
        <div className="cart-page">
          <div className="cart-container">
            <div className="empty-cart">
              <div className="empty-cart-icon">🛒</div>
              <h2>Your cart is empty</h2>
              <p>Looks like you haven't added anything to your cart yet</p>
              <Link to="/ready-made-products" className="continue-shopping-btn">
                <svg viewBox="0 0 24 24">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                </svg>
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <FurnitureNavbar />
      <div className="cart-page">
        <div className="cart-container">
          {/* Cart Header */}
          <div className="cart-header">
            <div className="header-left">
              {/* <Link to="/ready-made-products" className="back-btn">
                <svg viewBox="0 0 24 24">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                </svg>
                Continue Shopping
              </Link> */}
              <div className="cart-title">
                <h1>Your Cart</h1>
                <p className="item-count">
                  <span>{cartItems.length}</span>{' '}
                  {cartItems.length === 1 ? 'item' : 'items'} total
                  {selectedCount > 0 && (
                    <>
                      {' '}
                      · <span>{selectedCount}</span> selected
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="header-actions">
              {selectedCount > 0 && (
                <button className="remove-selected-btn" onClick={handleRemoveSelected}>
                  Remove Selected ({selectedCount})
                </button>
              )}
              <button className="clear-cart-btn" onClick={handleClearCart}>
                <svg viewBox="0 0 24 24">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                </svg>
                Clear Cart
              </button>
            </div>
          </div>

          {/* Cart Items */}
          <div className="cart-items-section">
            {/* Select All Bar */}
            {cartItems.some((item) => item.inStock) && (
              <div className="select-all-bar">
                <div className="select-all-left">
                  <label className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={toggleSelectAll}
                      disabled={availableItemsCount === 0}
                    />
                    <span>Select All Items</span>
                  </label>
                </div>
                <div className="selected-count">
                  {selectedCount} of {availableItemsCount} selected
                </div>
              </div>
            )}

            {cartItems.map((item) => (
              <div
                key={item.id}
                className={`cart-item ${!item.inStock ? 'disabled' : ''}`}
              >
                <div className="item-checkbox">
                  <label className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={() => toggleItemSelection(item.id)}
                      disabled={!item.inStock}
                    />
                  </label>
                </div>

                <div className="item-image">
                  {typeof item.image === 'string' && item.image.startsWith('http') ? (
                    <img src={item.image} alt={item.name} />
                  ) : (
                    <span>{item.image}</span>
                  )}
                </div>

                <div className="item-details">
                  <div className="item-info">
                    <h3 className="item-name">{item.name}</h3>
                    <span className="item-category">{item.category}</span>
                    <div className="item-price">KSh {item.price.toLocaleString()}</div>
                  </div>

                  <div
                    className={`stock-status ${
                      !item.inStock
                        ? 'out-of-stock'
                        : item.stockCount <= 3
                          ? 'low-stock'
                          : 'in-stock'
                    }`}
                  >
                    {!item.inStock ? (
                      <>❌ Out of Stock</>
                    ) : item.stockCount <= 3 ? (
                      <>⚠️ Only {item.stockCount} left in stock</>
                    ) : (
                      <>✓ In Stock</>
                    )}
                  </div>
                </div>

                <div className="item-actions">
                  <div className="quantity-control">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1 || !item.inStock}
                    >
                      −
                    </button>
                    <span>{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      disabled={
                        !item.inStock || item.quantity >= item.stockCount
                      }
                    >
                      +
                    </button>
                  </div>

                  <div className="item-total">
                    KSh {(item.price * item.quantity).toLocaleString()}
                  </div>

                  <button
                    className="remove-item"
                    onClick={() => removeItem(item.id)}
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Cart Summary */}
          <div className="cart-summary">
            <h2>Order Summary</h2>

            <div className="selected-items-info">
              {selectedCount === 0 ? (
                <span>No items selected</span>
              ) : (
                <>
                  Checking out with <span>{selectedCount}</span> selected
                  item(s)
                </>
              )}
            </div>

            <div className="summary-row">
              <span className="label">
                <svg viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                Subtotal
              </span>
              <span className="amount">
                KSh {calculateSelectedSubtotal().toLocaleString()}
              </span>
            </div>

            <div className="summary-row">
              <span className="label">
                <svg viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 13h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                Discount
              </span>
              <span className="amount">
                KSh {calculateSelectedDiscount().toLocaleString()}
              </span>
            </div>

            <div className="summary-row">
              <span className="label">
                <svg viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
                Tax (16%)
              </span>
              <span className="amount">
                KSh {calculateSelectedTax().toLocaleString()}
              </span>
            </div>

            <div className="summary-row total">
              <span>Total</span>
              <span className="amount">
                KSh {calculateSelectedTotal().toLocaleString()}
              </span>
            </div>

            <button 
              className="checkout-btn" 
              disabled={selectedCount === 0}
              onClick={handleCheckout}
            >
              {selectedCount === 0
                ? 'Select Items to Checkout'
                : 'Proceed to Checkout'}
              <svg viewBox="0 0 24 24">
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Clear Cart Confirmation Modal */}
      <Modal
        show={showClearCartModal}
        onHide={() => setShowClearCartModal(false)}
        centered
        className="delete-modal"
      >
        <Modal.Header closeButton>
          <h2>Clear Cart</h2>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to clear your entire cart? 
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowClearCartModal(false)}
            disabled={actionLoading}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={confirmClearCart}
            disabled={actionLoading}
          >
            {actionLoading ? 'Clearing...' : 'Clear Cart'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Remove Selected Items Confirmation Modal */}
      <Modal
        show={showRemoveSelectedModal}
        onHide={() => setShowRemoveSelectedModal(false)}
        centered
        className="delete-modal"
      >
        <Modal.Header closeButton>
          <h2>Remove Items</h2>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to remove <strong>{selectedCount}</strong> selected item(s) from your cart?
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowRemoveSelectedModal(false)}
            disabled={actionLoading}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={confirmRemoveSelected}
            disabled={actionLoading}
          >
            {actionLoading ? 'Removing...' : 'Remove Items'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withAuth(CartPage, 'client');